import * as THREE from 'three';
import { IslandWorld } from './islandWorld';
import { Player } from './player';
import { Physics } from './physics';
import { ModelLoader } from './modelLoader';
import { AudioManagerInstance } from './audioManager';
import { settingsStore } from './settingsStore';
import { isMobileDevice } from './device';

const PLAYER_HEIGHT = 1.75;
const DEFAULT_RELATIVE_SPAWN_Y = 36;

const findSafeSpawnCameraY = (world, islandData, spawnX, spawnZ) => {
  const offsets = [
    [0, 0], [2, 0], [-2, 0], [0, 2], [0, -2],
    [4, 0], [-4, 0], [0, 4], [0, -4], [3, 3], [-3, 3], [3, -3], [-3, -3],
  ];
  const minY = Math.floor(islandData.coords.y);
  const maxY = Math.floor(islandData.coords.y + 63);

  for (const [dx, dz] of offsets) {
    const x = Math.floor(spawnX + dx);
    const z = Math.floor(spawnZ + dz);

    for (let y = maxY; y >= minY; y--) {
      const ground = world.getBlock(x, y, z)?.id ?? 0;
      if (ground === 0) continue;

      const head1 = world.getBlock(x, y + 1, z)?.id ?? 0;
      const head2 = world.getBlock(x, y + 2, z)?.id ?? 0;
      if (head1 === 0 && head2 === 0) {
        return y + 0.5 + PLAYER_HEIGHT + 0.05;
      }
    }
  }

  return islandData.coords.y + DEFAULT_RELATIVE_SPAWN_Y;
};


export const initIslandGame = async (user, islandData) => {
  const container = document.getElementById('island-game');
  const isMobile = isMobileDevice();
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const renderer = new THREE.WebGLRenderer({
    antialias: !isMobile,
    powerPreference: 'high-performance',
    precision: (isMobile && !isIOS) ? 'mediump' : 'highp',
  });
  const getRenderSize = () => ({
    width: Math.max(1, container?.clientWidth || window.innerWidth),
    height: Math.max(1, container?.clientHeight || window.innerHeight),
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
  {
    const { width, height } = getRenderSize();
    renderer.setSize(width, height);
  }
  renderer.setClearColor(0x87ceeb);
  renderer.shadowMap.enabled = !isMobile;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  container?.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x87ceeb);
  scene.fog = new THREE.Fog(0x87ceeb, 0, 750);


  // 🌍 World + Player
  const world = new IslandWorld(islandData);
  if (isMobile) {
    world.drawDistance = 1;
  }
  await world.generate();
  scene.add(world);

  const player = new Player(scene, world, user.uid);

  // Validate coords exist (islands are floating structures, not at 0,0,0)
  if (!islandData.coords || typeof islandData.coords.x !== 'number') {
    throw new Error('Island coords are missing or invalid. Cannot initialize island.');
  }

  const spawnX = islandData.coords.x + 32;
  const spawnZ = islandData.coords.z + 32;
  const safeCameraY = findSafeSpawnCameraY(world, islandData, spawnX, spawnZ);
  player.teleportToIsland({
    x: spawnX,
    // teleportToIsland currently adds +2 internally; offset so final camera Y is safeCameraY.
    y: safeCameraY - 2,
    z: spawnZ,
  });
  player.velocity.set(0, 0, 0);
  AudioManagerInstance.initListener(player.camera);

  // Load settings and initialize AudioManager settings subscription
  await settingsStore.load(user.uid);
  AudioManagerInstance.initSettings(settingsStore);

  // Define sounds to load for the main game
  const mainGameSounds = [
    { name: 'island_block_break', filePath: '/sounds/block_break.wav' },
    { name: 'island_block_place', filePath: '/sounds/block_place.wav' },
    { name: 'island_background_music', filePath: '/sounds/background_music.mp3' },
  ];

  // Load sounds using the AudioManager singleton
  await AudioManagerInstance.loadSounds(mainGameSounds);

  AudioManagerInstance.playSound('island_background_music', 0.5, true);


  const physics = new Physics(scene);


  // 🧰 Tool loader
  new ModelLoader(models => {
    player.setTool(models.pickaxe);
  });

  // 🌞 Lighting

  const sun = new THREE.DirectionalLight(0xffffff, 0.8);
  sun.position.set(50, 200, 100);
  sun.castShadow = true;
  sun.shadow.mapSize.width = 2048;
  sun.shadow.mapSize.height = 2048;
  scene.add(sun);

  const ambient = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambient);

  // 🌀 Animate loop
  let prevTime = performance.now();
  let animationFrameId;

  function animate() {
    animationFrameId = requestAnimationFrame(animate);
    const now = performance.now();
    const dt = (now - prevTime) / 1000;

    if (player.controls.isLocked || player.isMobile) {
      physics.update(dt, player, world);
      player.update(world);
    }

    renderer.render(scene, player.camera);
    prevTime = now;
  }

  animate();

  // 📏 Handle Resize
  const handleResize = () => {
    const { width, height } = getRenderSize();
    player.camera.aspect = width / height;
    player.camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  };

  window.addEventListener('resize', handleResize);
  handleResize();

  // 🧹 Cleanup on unmount
  return {
    cleanup: () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      if (renderer.domElement.parentNode) {
        renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
      AudioManagerInstance.dispose();
      renderer.dispose?.();
    },
    player: player,
    resize: handleResize
  };
};
